import InputContainer from "./InputContainer";
import { twMerge } from "tailwind-merge";
import * as React from "react";
import { Constants } from "../utils";

export type PhoneNumberFieldClassNames = {
  root?: string;
  label?: string;
  container?: string;
  error?: string;
  flag?: string;
  divider?: string;
  input?: string;
};

export type PhoneNumberFieldProps = {
  label?: string;
  errorMessage?: string;
  classNames?: PhoneNumberFieldClassNames;
} & Omit<React.ComponentPropsWithoutRef<"input">, "type">;

function getFlagFromPhone(value: string | undefined): string {
  const v = (value ?? "").replace(/\s+/g, "");
  if (!v.startsWith("+")) return "🏳️";
  const hit = Constants.phoneMeta.find(({ dial }) => v.startsWith(dial));
  return hit?.flag ?? "🏳️";
}

function formatWith(meta: Constants.PhoneMeta, nationalDigits: string): string {
  if (!meta.nationalFormat) return nationalDigits;
  return nationalDigits.replace(
    meta.nationalFormat.re,
    meta.nationalFormat.fmt,
  );
}

function sanitizeAndFormatPhone(raw: string): string {
  const trimmed = raw.trim();
  const digits = trimmed.replace(/\D/g, "");
  if (!digits) return "";

  const all = `+${digits}`;
  const meta = Constants.phoneMeta.find(({ dial }) => all.startsWith(dial));
  if (!meta) return all;

  const dialDigits = meta.dial.replace(/\D/g, "");
  const national = digits
    .slice(dialDigits.length)
    .slice(0, meta.nationalMaxDigits);

  return `${meta.dial}${formatWith(meta, national)}`;
}

function handlePhoneChange(
  e: React.ChangeEvent<HTMLInputElement>,
  args: {
    onChange?: React.ComponentPropsWithoutRef<"input">["onChange"];
    isControlled: boolean;
    setInternalValue: React.Dispatch<React.SetStateAction<string>>;
  },
) {
  const raw = e.currentTarget.value;
  const next = sanitizeAndFormatPhone(raw);
  if (next !== raw) e.currentTarget.value = next;
  if (!args.isControlled) args.setInternalValue(next);
  args.onChange?.(e);
}

export default function PhoneNumberField({
  id,
  label,
  errorMessage,
  classNames,
  required,
  className,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  value,
  defaultValue,
  onChange,
  onPaste,
  onKeyDown,
  inputMode,
  pattern,
  ...inputProps
}: PhoneNumberFieldProps) {
  const isControlled = typeof value === "string";
  const [internalValue, setInternalValue] = React.useState<string>(
    typeof defaultValue === "string"
      ? defaultValue
      : "+1",
  );
  const currentValue = isControlled ? value : internalValue;
  const flag = getFlagFromPhone(currentValue);

  const handlePaste = React.useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      onPaste?.(e);
      if (e.defaultPrevented) return;
      const text = e.clipboardData.getData("text");
      const sanitized = sanitizeAndFormatPhone(text);
      if (sanitized === text) return;
      e.preventDefault();
      const next = sanitizeAndFormatPhone(`${currentValue}${sanitized}`);
      if (!isControlled) setInternalValue(next);
      (e.currentTarget as HTMLInputElement).value = next;
    },
    [onPaste, currentValue, isControlled],
  );

  return (
    <InputContainer
      id={id}
      label={label}
      required={required}
      errorMessage={errorMessage}
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
      classNames={classNames}
      className={className}
    >
      <div
        className={twMerge(
          "w-[39px] h-full flex items-center justify-center border-r-[1px] border-input-border",
          classNames?.flag,
        )}
        aria-hidden="true"
      >
        {flag}
      </div>
      <InputContainer.Input
        {...inputProps}
        type="tel"
        required={required}
        value={currentValue}
        inputMode={inputMode ?? "tel"}
        pattern={pattern ?? "[0-9+\\s()-]*"}
        className={twMerge(classNames?.input)}
        onPaste={handlePaste}
        onChange={(e) =>
          handlePhoneChange(e, { onChange, isControlled, setInternalValue })
        }
      />
    </InputContainer>
  );
}
